/**
 * @fileoverview Mounts a `<ViolationCaptureBlock>` per failing mapping.
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - For widgets whose `violationMappings[]` include any mapping that fires
 *   on the current value, failure-capable toggles whose configured fail side
 *   is selected, or any mapping with an existing logged violation, mount one
 *   `<ViolationCaptureBlock>` per active mapping.
 * - Honor the per-mapping `allowMultipleInstances` flag — when true, the
 *   block keeps the "Log Violation" affordance available after each
 *   occurrence is logged; when false, the block hides the form after one
 *   occurrence is captured.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `@firesquirrel/core-domain` `getFailingMappings`.
 * - `useAttachmentPicker`, `useIsOffline` from rendererContext.
 * - `useViolationsCapture` for adapter access.
 * - `<ViolationCaptureBlock>` for the capture surface.
 *
 * Architecture:
 * - Presentational dispatch. State lives in the host's PersistAdapter via
 *   `useViolationsCapture`. Hidden / disabled / N/A short-circuit.
 *
 * Notes:
 * - Returns null when there are no failing mappings / toggle failures and no
 *   existing violations to manage — the widget below renders alone.
 * - `<FieldRenderer>` mounts this AFTER every widget so the capture surface
 *   appears immediately below the field that triggered it. This is the
 *   single place where the inline capture is wired; widgets do NOT have to
 *   know about it.
 *
 * @component FieldViolationCaptures
 */
import { type ReactNode } from 'react';
import {
  evaluateCondition,
  getFailingMappings,
  type Condition,
  type FieldResponse,
  type InspectionFormField,
  type ResolvedFieldStateV2,
  type ViolationMapping,
} from '@firesquirrel/core-domain';
import { ViolationCaptureBlock } from './ViolationCaptureBlock';
import { UNMAPPED_FAILURE_CODE_SET_ID } from './findViolationForMapping';
import { useAttachmentPicker, useIsOffline } from '../contracts/rendererContext';
import { useViolationsCapture } from './violationsCaptureContext';
import { isPassFailField, isPassFailFailing, resolveToggleFailsOn } from './passFailState';

export interface FieldViolationCapturesProps {
  field: InspectionFormField;
  value: unknown;
  isNA?: boolean;
  resolved?: ResolvedFieldStateV2;
  formId: string;
  bulkPilotMode?: boolean;
}

function shouldHide(resolved?: ResolvedFieldStateV2, isNA?: boolean): boolean {
  return !!(resolved?.hidden || resolved?.disabled || isNA);
}

function readBoolean(extras: Record<string, unknown> | undefined, key: string, fallback: boolean): boolean {
  const value = extras?.[key];
  return typeof value === 'boolean' ? value : fallback;
}

function evaluateConditionalBehaviors(
  field: InspectionFormField,
  responses: Record<string, FieldResponse>,
): { showNotes?: boolean; showPhotos?: boolean; requireNotes?: boolean; requirePhotos?: boolean } {
  const behaviors = (field.extras?.conditionalBehaviors as Array<Record<string, unknown>> | undefined) ?? [];
  let showNotes: boolean | undefined;
  let showPhotos: boolean | undefined;
  let requireNotes: boolean | undefined;
  let requirePhotos: boolean | undefined;

  for (const b of behaviors) {
    if (b.when && typeof b.when === 'object') {
      if (evaluateCondition(b.when as Condition, responses)) {
        if (b.show_notes !== undefined) showNotes = Boolean(b.show_notes);
        if (b.show_photos !== undefined) showPhotos = Boolean(b.show_photos);
        if (b.require_notes !== undefined) requireNotes = Boolean(b.require_notes);
        if (b.require_photos !== undefined) requirePhotos = Boolean(b.require_photos);
      }
    }
  }

  return { showNotes, showPhotos, requireNotes, requirePhotos };
}

function getEffectiveMapping(mapping: ViolationMapping, extras: Record<string, unknown> | undefined): ViolationMapping {
  return {
    ...mapping,
    requireNotes: readBoolean(extras, 'requireNotesOnFail', mapping.requireNotes),
    requirePhotos: readBoolean(extras, 'requirePhotosOnFail', mapping.requirePhotos),
    requireLocation: readBoolean(extras, 'requireLocationOnFail', !!mapping.requireLocation),
    requireCorrectiveAction: readBoolean(extras, 'requireCorrectiveActionOnFail', !!mapping.requireCorrectiveAction),
  };
}

/**
 * Build a synthetic "unmapped failure" capture mapping for a pass/fail field
 * that has NO bound violation code. The capture surface still appears
 * whenever the field is in a fail state — the pass/fail state itself is the
 * trigger; the violation code is just optional metadata.
 *
 * Returns null for fields that are not pass/fail capable or do not have
 * `extras.hasPassFail` enabled.
 */
function getUnmappedFailureMapping(field: InspectionFormField): ViolationMapping | null {
  if (!isPassFailField(field)) return null;
  const source = field.violationMappings?.[0];
  // The trigger is decorative here — we evaluate `isPassFailFailing` directly
  // rather than running `evaluateCondition`. We still set a value so consumers
  // that inspect the mapping see something coherent.
  const extras = (field.extras ?? {}) as { failsOn?: boolean; failValues?: string[] };
  const triggerValue = field.type === 'toggle'
    ? resolveToggleFailsOn(field)
    : (Array.isArray(extras.failValues) && extras.failValues.length > 0 ? extras.failValues[0] : '');
  return getEffectiveMapping({
    codeSetId: UNMAPPED_FAILURE_CODE_SET_ID,
    code: `field:${field.id}`,
    trigger: { field: field.id, op: 'equals', value: triggerValue },
    requireNotes: source?.requireNotes ?? false,
    requirePhotos: source?.requirePhotos ?? false,
    // Location is core to the unmapped-failure capture surface (where was
    // the problem?) — seeded required unless the field explicitly opts out
    // via extras.requireLocationOnFail === false (resolved below).
    requireLocation: source?.requireLocation ?? true,
    // requireCorrectiveAction is overridden by getEffectiveMapping when
    // field.extras.requireCorrectiveActionOnFail is set, so the source
    // value here is just the seed default.
    requireCorrectiveAction: source?.requireCorrectiveAction ?? false,
    allowMultipleInstances: source?.allowMultipleInstances ?? true,
  }, field.extras);
}

function mappingKey(mapping: ViolationMapping): string {
  return `${mapping.codeSetId}\u0000${mapping.code}`;
}

function isUnmappedFailureMapping(mapping: ViolationMapping): boolean {
  return mapping.codeSetId === UNMAPPED_FAILURE_CODE_SET_ID;
}

export function FieldViolationCaptures(props: FieldViolationCapturesProps): ReactNode {
  const { field, value, isNA, resolved, formId, bulkPilotMode } = props;
  const ctx = useViolationsCapture();
  const attachmentPicker = useAttachmentPicker() ?? undefined;
  const isOffline = useIsOffline();

  if (shouldHide(resolved, isNA)) return null;

  // Build the response map the engine expects (single-entry suffices since
  // mappings reference only the field itself today).
  const responses: Record<string, FieldResponse> = {
    [field.id]: { value, isNA: !!isNA },
  };
  const failing = getFailingMappings(field, responses);
  const failingKeys = new Set(failing.map(mappingKey));

  // The "this field has a pass/fail state" checkbox is the single source of
  // truth for whether a pass/fail-capable widget (toggle / dropdown / radio /
  // checkboxGroup) is currently failing. Bound violation codes are optional
  // metadata; they do NOT gate the failure capture surface.
  const passFailEnabled = isPassFailField(field);
  const passFailIsFailing = passFailEnabled && isPassFailFailing(field, value);

  const fallbackBehaviors = evaluateConditionalBehaviors(field, responses);

  const fieldShowNotes = (resolved?.showNotes ?? fallbackBehaviors.showNotes ?? readBoolean(field.extras, 'showNotes', false)) || readBoolean(field.extras, 'showNotesAlways', false);
  const fieldShowPhotos = (resolved?.showPhotos ?? fallbackBehaviors.showPhotos ?? readBoolean(field.extras, 'showPhotos', false)) || readBoolean(field.extras, 'showCameraAlways', false);
  const fieldRequireNotes = (resolved?.requireNotes ?? fallbackBehaviors.requireNotes ?? readBoolean(field.extras, 'requireNotes', false)) || readBoolean(field.extras, 'requireNotesOnFail', false);
  const fieldRequirePhotos = (resolved?.requirePhotos ?? fallbackBehaviors.requirePhotos ?? readBoolean(field.extras, 'requirePhotos', false)) || readBoolean(field.extras, 'requirePhotosOnFail', false);

  // An unmapped (no bound violation code) pass/fail field only gets a capture
  // block when the template author explicitly configured a failure-capture
  // behavior on it — pass/fail state alone must not conjure photos/location UI.
  const hasExplicitUnmappedCaptureBehavior =
    fieldShowNotes
    || fieldShowPhotos
    || fieldRequireNotes
    || fieldRequirePhotos
    || readBoolean(field.extras, 'requireLocationOnFail', false)
    || readBoolean(field.extras, 'requireCorrectiveActionOnFail', false);

  const hasBoundMappings = (field.violationMappings ?? []).some((mapping) => mapping.bindingMode !== 'pending-tenant-bind');
  const unmappedFailureMapping = getUnmappedFailureMapping(field);
  const existingUnmappedFailure = unmappedFailureMapping ? ctx?.getViolation(field.id, unmappedFailureMapping) ?? null : null;

  // When a pass/fail field has bound mapping(s), "isFailing" reflects the
  // pass/fail state itself, not whichever mapping triggers happen to fire.
  // This keeps the capture surface in lockstep with the FieldWrapper pill.
  const activeMappings = (field.violationMappings ?? [])
    .filter((mapping) => mapping.bindingMode !== 'pending-tenant-bind')
    .map((mapping) => {
      const effectiveMapping = getEffectiveMapping(mapping, field.extras);
      const existing = ctx?.getViolation(field.id, effectiveMapping) ?? null;
      const isFailing = passFailEnabled ? passFailIsFailing : failingKeys.has(mappingKey(mapping));
      return { effectiveMapping, existing, isFailing };
    })
    .filter(({ existing, isFailing }) => isFailing || !!existing || fieldShowNotes || fieldShowPhotos);

  // Pass/fail-aware fields that have no bound mapping still need an inline
  // capture surface when a synthetic occurrence already exists (it must stay
  // manageable) or when the field is currently failing AND an explicit
  // failure-capture behavior is configured.
  if (
    unmappedFailureMapping
    && (!hasBoundMappings || existingUnmappedFailure)
    && (
      !!existingUnmappedFailure
      || (passFailIsFailing && hasExplicitUnmappedCaptureBehavior)
    )
  ) {
    activeMappings.push({
      effectiveMapping: unmappedFailureMapping,
      existing: existingUnmappedFailure,
      isFailing: passFailIsFailing,
    });
  }

  if (activeMappings.length === 0) return null;

  return (
    <>
      {activeMappings.map(({ effectiveMapping, existing, isFailing }) => {
        const unmappedFailure = isUnmappedFailureMapping(effectiveMapping);
        const allowMultiple = effectiveMapping.allowMultipleInstances ?? false;
        const showNotes = fieldShowNotes || unmappedFailure || readBoolean(field.extras, 'showNotesAlways', false) || effectiveMapping.requireNotes;
        const showPhotos = fieldShowPhotos || unmappedFailure || readBoolean(field.extras, 'showCameraAlways', false) || effectiveMapping.requirePhotos;
        const requireNotes = fieldRequireNotes || effectiveMapping.requireNotes;
        const requirePhotos = fieldRequirePhotos || effectiveMapping.requirePhotos;
        // Corrective action visibility is now driven SOLELY by the per-widget
        // "Require corrective action on fail" config (extras.requireCorrectiveActionOnFail,
        // mirrored to mapping.requireCorrectiveAction). The legacy module-level
        // toggle has been removed — widgets that can fail are the only place
        // template authors configure corrective action capture.
        const showCorrectiveActionField = !!effectiveMapping.requireCorrectiveAction;
        const occurrenceLogged = (existing?.occurrences.length ?? 0) > 0;
        const blockReadOnly =
          !!ctx?.readOnly ||
          !!bulkPilotMode;
        const allowNewOccurrence = isFailing && (allowMultiple || !occurrenceLogged);
        return (
          <ViolationCaptureBlock
            key={`${field.id}.${effectiveMapping.codeSetId}.${effectiveMapping.code}`}
            fieldId={field.id}
            formId={formId}
            mapping={effectiveMapping}
            violation={existing}
            inspectorId={ctx?.inspectorId ?? 'inspector'}
            eventIndex={ctx?.eventIndex ?? 0}
            readOnly={blockReadOnly}
            allowNewOccurrence={allowNewOccurrence}
            isOffline={isOffline}
            attachmentPicker={attachmentPicker}
            isFailing={isFailing}
            showNotes={showNotes}
            showPhotos={showPhotos}
            requireNotes={requireNotes}
            requirePhotos={requirePhotos}
            showCorrectiveAction={showCorrectiveActionField}
            onChange={(next) => ctx?.setViolation(field.id, effectiveMapping, next)}
          />
        );
      })}
    </>
  );
}
